// src/services/transactionHistoryService.js - SECURED
import { API_BASE_URL } from '../utils/constants';


const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/';
    }
    const error = await response.json();
    throw new Error(error.error || error.detail || 'Request failed');
  }
  return response.json();
};

export const transactionHistoryService = {
  /**
   * Get user's complete transaction history with filtering
   * @param {Object} params - Query parameters
   * @param {string} params.type - Transaction type filter (deposit, withdrawal, buy_stock, sell_stock)
   * @param {string} params.status - Status filter (pending, processing, completed, failed, cancelled)
   * @param {string} params.search - Search term
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   */
  async getUserTransactionHistory(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);

    const response = await fetch(
      `${API_BASE_URL}/transactions/history/?${queryParams}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      }
    );

    return handleResponse(response);
  },

  /**
   * Get transaction summary and statistics
   */
  async getTransactionSummary() {
    const response = await fetch(
      `${API_BASE_URL}/transactions/summary/`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      }
    );

    return handleResponse(response);
  },

  /**
   * Get user's recent transactions (last 10)
   */
  async getRecentTransactions() {
    const response = await fetch(
      `${API_BASE_URL}/transactions/recent/`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      }
    );

    return handleResponse(response);
  },

  /**
   * Get details of a specific transaction
   * @param {number} transactionId - Transaction ID
   */
  async getTransactionDetail(transactionId) {
    const response = await fetch(
      `${API_BASE_URL}/transactions/history/${transactionId}/`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
      }
    );

    return handleResponse(response);
  },

  /**
   * Get deposits only
   */
  async getDeposits(page = 1) {
    return await this.getUserTransactionHistory({ 
      type: 'deposit', 
      page 
    });
  },

  /**
   * Get withdrawals only
   */
  async getWithdrawals(page = 1) {
    return await this.getUserTransactionHistory({ 
      type: 'withdrawal', 
      page 
    });
  },

  /**
   * Get stock trades only
   */
  async getStockTrades(page = 1) {
    const buyStocks = await this.getUserTransactionHistory({ 
      type: 'buy_stock', 
      page 
    });
    const sellStocks = await this.getUserTransactionHistory({ 
      type: 'sell_stock', 
      page 
    });

    // Combine and sort by date
    const combined = [
      ...(buyStocks.results || buyStocks),
      ...(sellStocks.results || sellStocks)
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return {
      results: combined,
      count: combined.length
    };
  }
};